import { Rect } from '../../drawing';
import { GregorioWordPosition } from '../../gregorio-chant/structures/GregorioSyllable';
import { ChantContext } from '../../gregorio-chant/ChantContext';
import { LyricsTextMeasurer } from '../LyricsTextMeasurer';
import { DropCap } from './DropCap';
import { TextSpan, SPECIAL_CHARACTERS } from '../../gregorio-chant/structures/TextSpan';
import { ChantLayoutElement } from '../layout/ChantLayoutElement';

export class Lyric extends ChantLayoutElement {
  private _lyricType: GregorioWordPosition;
  private _spans: TextSpan[] = [];
  private _dominantBaseline = 'text-top';
  private _dropCapLen = 0;
  private _keepWithNext = false;

  public get text(): string {
    return this._spans.map(span => this._dropCapLen ? span.text.substring(this._dropCapLen) : span.text).join('');
  }

  private defaultStyle: { [key: string]: string };

  private get styleProperties(): string {
    return Object.keys(this.defaultStyle).map(key => key + ':' + this.defaultStyle[key]).join(';');
  }

  get allowsConnector() {
    return this._lyricType === GregorioWordPosition.BEGINNING || this._lyricType === GregorioWordPosition.WORD_MIDDLE;
  }

  get keepWithNext() {
    return this._keepWithNext;
  }

  constructor(
    context: ChantContext,
    spans: TextSpan[],
    lyricType: GregorioWordPosition,
    keepWithNext = false,
  ) {
    super(context);
    this._lyricType = lyricType;
    this._spans = spans;
    this._keepWithNext = keepWithNext;

    this.defaultStyle = {
      'font-family': this._context.lyricTextFont,
      'font-size': this._context.lyricTextSizePx + 'px',
      'font-kerning': 'normal',
    };
  }

  public performLayout(): void {
    this.recalculateMetrics();
  }

  protected recalculateMetrics() {
    LyricsTextMeasurer.instance.setElement(this.createSingleSvgFragment());
    const bbox = LyricsTextMeasurer.instance.element.getBBox();

    let offsetX = 0;
    const text = this.text;
    const hasCurlyBraces = text.indexOf('{') > -1 ? text.match(/\{([^{}]*)\}/g) : null;

    if (hasCurlyBraces) {
      const firstMatch = hasCurlyBraces[0];
      const len = firstMatch.length;
      const index = text.indexOf(firstMatch);
      offsetX = this.calculateOffset(index - 1 + (len - 1) / 2);
    } else {
      const result = this._context.language.findVowelSegment(text, 0);
      if (result.found) {
        offsetX = this.calculateOffset(result.startIndex);
      }
    }

    this.bounds = Rect.create(offsetX, this.bounds.y, bbox.width, this._context.lyricTextSizePx);
  }

  private calculateOffset(start: number): number {
    const startIndexFloor = Math.floor(start);
    const startIndexCeil = Math.ceil(start);
    if (startIndexCeil === startIndexFloor) {
      const wWithoutVowel = LyricsTextMeasurer.instance.element.getSubStringLength(0, startIndexFloor);
      const wWithVowel = LyricsTextMeasurer.instance.element.getSubStringLength(0, startIndexFloor + 1);
      const vowelWidth = wWithVowel - wWithoutVowel;
      return -wWithoutVowel + ((this._context.pitchWidth - vowelWidth) / 2);
    } else {
      const firstOffset = this.calculateOffset(startIndexFloor);
      const lastOffset = this.calculateOffset(startIndexCeil);
      return firstOffset + (start - startIndexFloor) * (lastOffset - firstOffset);
    }
  }

  generateDropCap() {
    const firstSpan = this._spans[0];
    const startsWithSpecialCharKey = Object.keys(SPECIAL_CHARACTERS)
      .find((start) => firstSpan.text.startsWith(start));
    this._dropCapLen = startsWithSpecialCharKey ? startsWithSpecialCharKey.length : 1;

    // if the dropcap is a single character syllable (vowel) that is the
    // beginning of the word, then we use a hyphen in place of the lyric text
    // and treat it as a single syllable.
    if (this.text.length === 1) {
      this._lyricType = GregorioWordPosition.WORD_ONE_SYLLABLE;
    }

    this.recalculateMetrics();

    return new DropCap(this._context, new TextSpan(
      firstSpan.text.substring(0, this._dropCapLen),
      firstSpan.type,
    ));
  }

  addConnector() {
    if (this._spans.length === 0) {
      return;
    }
    const lastSpan = this._spans[this._spans.length - 1];
    if (lastSpan.text.endsWith('-')) {
      return;
    }
    this._spans.push(new TextSpan('-'));
    this.recalculateMetrics();
  }

  private createSingleSvgFragment(): SVGElement {
    const textEl = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    textEl.setAttribute('x', String(this.bounds.x));
    textEl.setAttribute('y', String(this.bounds.y));
    textEl.setAttribute('text-anchor', 'start');
    textEl.setAttribute('dominant-baseline', this._dominantBaseline);
    textEl.setAttribute('style', this.styleProperties);

    let adjustment = 0;
    for (let i = 0; i < this._spans.length; i++) {
      let span = this._spans[i];
      if (this._dropCapLen > 0 && i === 0) {
        span = new TextSpan(span.text.substring(this._dropCapLen), span.type);
      }

      const [spanEements, newAdjustment] = span.getSvgSpan(this._context, false, {
        adjustment: adjustment,
        aAdjustment: 8,
        rAdjustment: 6,
        vAdjustment: 11,
      });

      adjustment = newAdjustment;

      for (const spanEl of spanEements) {
       if (spanEl) {
          textEl.appendChild(spanEl);
        }
      }
    }

    return textEl;
  }

  public createSvgFragments(): SVGElement[] {
    return [
      ...super.createSvgFragments(),
      this.createSingleSvgFragment(),
    ];
  }
}
